import {inject, Injectable} from '@angular/core';
import {ToastrService} from 'ngx-toastr';

const ERROR_TOAST_TIMEOUT = 8000;

interface StripeErrorResponse {
  error: string;
  name: string;
}

interface StripeError {
  toastr?: StripeErrorMessage;
  error?: stripe.Error;
}

enum StripeErrorType {
  CARD_ERROR = 'card_error',
  VALIDATION_ERROR = 'validation_error',
}

enum StripeErrorCode {
  CARD_DECLINED = 'card_declined',
}

export enum StripeErrorMessage {
  PAYMENT_DECLINED = 'Payment was declined. Please try a different card or contact your bank.',
  CARD_DECLINED = 'Your card could not be saved. Please contact your bank or try using a different payment method.',
  VALIDATION_ERROR = 'The data is incorrect. Please verify your information and try again.',
  INVALID_ADDRESS = 'The data is incorrect in one of the fields: Country, State or ZIP Code. Please update with the correct data, and submit again',
  INVALID_LOCATION = "Location isn't recognized. Please check your data",
  VERIFY_INFO = 'Please verify your info and re-submit the form.',
}

const stripeErrors: Record<string, StripeError> = {
  cardError: {
    toastr: StripeErrorMessage.PAYMENT_DECLINED,
    error: {
      message: null,
      type: StripeErrorType.CARD_ERROR,
      code: StripeErrorCode.CARD_DECLINED,
      charge: '',
    },
  },
  declined: {
    toastr: StripeErrorMessage.CARD_DECLINED,
    error: {
      message: null,
      type: StripeErrorType.CARD_ERROR,
      code: StripeErrorCode.CARD_DECLINED,
      charge: '',
    },
  },
  validationError: {
    toastr: StripeErrorMessage.VALIDATION_ERROR,
    error: {
      message: StripeErrorMessage.VERIFY_INFO,
      type: StripeErrorType.VALIDATION_ERROR,
      charge: '',
    },
  },
  invalidAddress: {
    toastr: StripeErrorMessage.INVALID_ADDRESS,
    error: {
      message: StripeErrorMessage.INVALID_LOCATION,
      type: StripeErrorType.VALIDATION_ERROR,
      charge: '',
    },
  },
  default: {
    toastr: StripeErrorMessage.VERIFY_INFO,
    error: {
      message: StripeErrorMessage.VERIFY_INFO,
      type: StripeErrorType.VALIDATION_ERROR,
      charge: '',
    },
  },
};

const errorTypes = {
  declined: [
    'has been declined',
    'CVC is incorrect',
    'number is invalid',
    'different card',
    'attempts too frequently',
    'your card issuer',
    'error occurred while processing',
  ],
  cardError: [
    'insufficient funds',
    'card was declined',
    'expiration date is incomplete',
    'card has expired',
    'error occurred while processing',
    'in `incomplete` status',
    'security code is incorrect',
  ],
  invalidAddress: ["location isn't recognized"],
};

@Injectable({
  providedIn: 'root',
})
export class StripeErrorsService {
  private toastr = inject(ToastrService);

  get verifyInfoError() {
    return stripeErrors.default.error;
  }

  get cardError() {
    return stripeErrors.cardError.error;
  }

  handleCardError(err: StripeErrorResponse | stripe.Error, onError: (err: {error: stripe.Error}) => void): void {
    const errorType = this.getErrorType(err);

    const {toastr, error} = stripeErrors[errorType] || stripeErrors.default;

    if (toastr) {
      this.showToastrOnError(toastr);
    }

    if (error) {
      onError({error});
    }
  }

  private getErrorType(err: any): string {
    const errorMessage = err?.error?.error || err?.message || '';

    const types = Object.entries(errorTypes);

    for (const [type, messages] of types) {
      if (messages.some(msg => errorMessage.includes(msg))) {
        return type;
      }
    }

    return 'default';
  }

  showToastrOnError(message: string) {
    return this.toastr.warning(message, null, {timeOut: ERROR_TOAST_TIMEOUT, positionClass: 'toast-center-fixed'});
  }
}
